import { Component, OnInit } from '@angular/core';
import { TagDefinitionService } from '@app/services/automation/tag-definition.service';
import { TagDefinition } from '@app/models/automation/tag-definition.model';
import { SystemType } from '@app/models/panelpricingwizard/system-type.model';
import { BaseDeviceType } from '@app/models/disciplinemanagement/base-device-type.model';
import { DeviceType } from '@app/models/disciplinemanagement/device-type.model';
import { DisciplineManagementService } from '@app/services/disciplinemanagement/disiplinemanagement.service';
import { DisciplineExtended } from '@app/models/disciplinemanagement/discipline-extended.model';
import { DnaAutomationSection } from '@app/models/dna/dna.automation-section.model';
import { DnaModuleService } from '@app/services/dna/dna.module.service';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { TagDataTypeHelper } from '@app/models/shared/tag-datatype.enum';
import {Tag} from "@app/models/automation/tag";
import swal from "sweetalert";

@Component({
  selector: 'app-tag-management',
  templateUrl: './tag-management.component.html',
  styleUrls: ['./tag-management.component.scss']
})
export class TagManagementComponent implements OnInit {
  tagDefinitions: TagDefinition[] = [];
  systemTypes: SystemType[] = [];
  baseDeviceTypes: BaseDeviceType[] = [];
  deviceTypes: DeviceType[] = [];
  disciplines: DisciplineExtended[] = [];
  sections: DnaAutomationSection[] = [];
  filteredSections: DnaAutomationSection[] = [];
  selectedDisciplineId: number = null;
  selectedSectionId: number = null;
  dataTypeOptions = TagDataTypeHelper.getAllOptions();
  selectedTagDefinition: TagDefinition = null;
  selectedTag: Tag = null;
  isEditingDefinition = false;
  isEditingTag = false;
  loading = false;

  /** Tag definition list filtering */
  tagDefinitionSearchText = '';

  private readonly allowedSectionNames = new Set(['doors', 'events', 'hardware']);

  constructor(
    private tagDefinitionService: TagDefinitionService,
    private disciplineManagementService: DisciplineManagementService,
    private dnaModuleService: DnaModuleService,
    private toastr: ToastrService,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.tagDefinitions = []; // Start with empty list
    this.loadDisciplines();
    this.loadSystemTypes();
    this.loadBaseDeviceTypes();
  }

  loadDisciplines() {
    this.disciplineManagementService.getDisciplines().subscribe(data => {
      this.disciplines = data || [];
    }, () => {
      this.toastr.error('Failed to load disciplines');
    });
  }

  loadBaseDeviceTypes() {
    this.disciplineManagementService.getBaseDeviceTypes().subscribe(data => {
      this.baseDeviceTypes = data || [];
    }, () => {
      this.toastr.error('Failed to load base device types');
    });
  }

  loadSystemTypes() {
    this.http.get<SystemType[]>('api/CommissioningSheetService/v2/getAllSystemTypes').subscribe(data => {
      this.systemTypes = data || [];
    }, () => {
      this.toastr.error('Failed to load system types');
    });
  }

  onDisciplineChange(disciplineId: number) {
    this.selectedDisciplineId = disciplineId;
    this.selectedSectionId = null;
    this.filteredSections = [];
    this.tagDefinitions = [];
    this.selectedTagDefinition = null;
    this.tagDefinitionSearchText = '';

    if (disciplineId) {
      this.dnaModuleService.getSectionsByDiscipline(disciplineId).subscribe(data => {
        const sections = data || [];
        // Only show Doors, Events and Hardware sections
        this.filteredSections = sections.filter(s => this.allowedSectionNames.has((s?.name || '').toLowerCase()));
      }, () => {
        this.toastr.error('Failed to load sections');
      });
    }
  }

  onSectionChange(sectionId: number) {
    this.selectedSectionId = sectionId;
    this.selectedTagDefinition = null;
    this.tagDefinitionSearchText = '';

    // load device types for the selected discipline/section
    this.deviceTypes = [];
    if (this.selectedDisciplineId && sectionId) {
      this.disciplineManagementService.getDisciplineDeviceTypes(this.selectedDisciplineId, sectionId).subscribe(d => {
        this.deviceTypes = d?.deviceTypes || [];
      }, () => {
        this.toastr.error('Failed to load device types');
      });
    }
    
    if (sectionId) {
      this.loadTagDefinitionsBySection(sectionId);
    } else {
      this.tagDefinitions = [];
    }
  }

  get filteredDeviceTypes(): DeviceType[] {
    if (this.selectedTagDefinition && this.selectedTagDefinition.baseDeviceTypeId) {
      return (this.deviceTypes || []).filter(dt => dt.baseDeviceType.id === this.selectedTagDefinition.baseDeviceTypeId);
    }
    return this.deviceTypes || [];
  }

  loadTagDefinitionsBySection(sectionId: number) {
    this.loading = true;
    this.tagDefinitionService.getAllBySection(sectionId).subscribe(data => {
      // Clear tags from loaded definitions to prevent auto-loading
      this.tagDefinitions = (data || []).map(td => ({
        ...td,
        tags: [] // Don't load tags initially
      }));
      this.loading = false;
    }, () => {
      this.toastr.error('Failed to load tag definitions');
      this.loading = false;
    });
  }

  get filteredBaseDeviceTypes(): BaseDeviceType[] {
    if (this.selectedSectionId) {
      return this.baseDeviceTypes.filter(b => b.section?.id === this.selectedSectionId);
    }
    return this.baseDeviceTypes;
  }

  get filteredSystemTypes(): SystemType[] {
    // Filter system types by the selected discipline
    // This ensures only system types belonging to the discipline of the selected section are shown
    if (this.selectedDisciplineId) {
      return this.systemTypes.filter(st => st.disciplineId === this.selectedDisciplineId);
    }
    return this.systemTypes;
  }

  get filteredTagDefinitions(): TagDefinition[] {
    const defs = this.tagDefinitions || [];

    const q = (this.tagDefinitionSearchText || '').trim().toLowerCase();
    if (!q) return defs;

    return defs.filter(d => {
      const engineerName = (d as any).engineerFriendlyName?.toString().toLowerCase() || '';
      const baseTypeName = (d as any).baseDeviceTypeName?.toString().toLowerCase() || '';
      const dataType = (d as any).dataType?.toString().toLowerCase() || '';
      const tagCount = ((d as any).tagCount ?? '').toString().toLowerCase();

      return (
        engineerName.includes(q) ||
        baseTypeName.includes(q) ||
        dataType.includes(q) ||
        tagCount.includes(q)
      );
    });
  }

  selectTagDefinition(tagDefinition: TagDefinition) {
    this.selectedTagDefinition = tagDefinition;
    this.selectedTag = null;
    this.isEditingDefinition = false;
    this.isEditingTag = false;
    
    // Load full details including tags when a tag definition is selected
    if (tagDefinition && tagDefinition.id > 0) {
      this.tagDefinitionService.getById(tagDefinition.id).subscribe(fullData => {
        this.selectedTagDefinition = fullData;
      }, () => {
        this.toastr.error('Failed to load tag definition details');
      });
    }
  }

  newTagDefinition() {
    if (!this.selectedSectionId) {
      this.toastr.warning('Please select a Section first');
      return;
    }
    
    this.selectedTagDefinition = {
      id: 0,
      engineerFriendlyName: '',
      dataType: '',
      defaultValue: '',
      baseDeviceTypeId: null,
      createdDate: new Date(),
      createdBy: 0,
      tags: []
    };
    this.isEditingDefinition = true;
  }

  editTagDefinition() {
    if (!this.selectedTagDefinition) {
      return;
    }
    this.isEditingDefinition = true;
  }

  saveTagDefinition() {
    if (!this.selectedTagDefinition.engineerFriendlyName) {
      this.toastr.warning('Engineer Friendly Name is required');
      return;
    }

    if (!this.selectedTagDefinition.baseDeviceTypeId) {
      this.toastr.warning('Base Device Type is required');
      return;
    }

    const isNew = this.selectedTagDefinition.id === 0;
    
    if (isNew) {
      this.tagDefinitionService.create(this.selectedTagDefinition).subscribe(result => {
        this.toastr.success('Tag Definition created successfully');
        // Reload tag definitions list
        if (this.selectedSectionId) {
          this.loadTagDefinitionsBySection(this.selectedSectionId);
        }
        // Load the newly created tag definition details
        this.tagDefinitionService.getById(result.id).subscribe(fullData => {
          this.selectedTagDefinition = fullData;
          this.isEditingDefinition = false;
        });
      }, error => {
        this.toastr.error('Failed to create tag definition');
      });
    } else {
      this.tagDefinitionService.update(this.selectedTagDefinition).subscribe(result => {
        this.toastr.success('Tag Definition updated successfully');
        // Reload tag definitions list
        if (this.selectedSectionId) {
          this.loadTagDefinitionsBySection(this.selectedSectionId);
        }
        // Reload the current tag definition details
        this.tagDefinitionService.getById(this.selectedTagDefinition.id).subscribe(fullData => {
          this.selectedTagDefinition = fullData;
          this.isEditingDefinition = false;
        });
      }, error => {
        this.toastr.error('Failed to update tag definition');
      });
    }
  }

  cancelEditDefinition() {
    this.isEditingDefinition = false;
    if (this.selectedTagDefinition.id === 0) {
      this.selectedTagDefinition = null;
    }
  }

  deleteTagDefinition(tagDefinition: TagDefinition) {
    swal({
      title: 'Are you sure?',
      text: `Do you want to delete "${tagDefinition.engineerFriendlyName}"?`,
      icon: 'warning',
      buttons: {
        cancel: {
          text: 'No',
          visible: true,
          closeModal: true
        },
        confirm: {
          text: 'Yes',
          value: true,
          visible: true,
          className: 'bg-danger',
          closeModal: true
        }
      }
    }).then((isConfirm) => {
      if (isConfirm) {
        this.tagDefinitionService.delete(tagDefinition.id).subscribe(result => {
          this.toastr.success('Tag Definition deleted successfully');
          if (this.selectedSectionId) {
            this.loadTagDefinitionsBySection(this.selectedSectionId);
          }
          if (this.selectedTagDefinition?.id === tagDefinition.id) {
            this.selectedTagDefinition = null;
          }
        }, error => {
          this.toastr.error('Failed to delete tag definition');
        });
      }
    });
  }

  newTag() {
    if (!this.selectedTagDefinition || this.selectedTagDefinition.id === 0) {
      this.toastr.warning('Please save the tag definition first');
      return;
    }

    this.selectedTag = {
      id: 0,
      name: '',
      description: '',
      dataType: this.selectedTagDefinition.dataType || '',
      defaultValue: this.selectedTagDefinition.defaultValue || '',
      tagDefinitionId: this.selectedTagDefinition.id,
      typeIds: [] // Initialize empty array for typeIds
    } as any;
    this.isEditingTag = true;
  }

  editTag(tag: Tag) {
    // Deep copy the tag and ensure typeIds array is properly copied
    this.selectedTag = { 
      ...tag,
      typeIds: tag.typeIds ? [...tag.typeIds] : []
    };
    this.isEditingTag = true;
  }

  saveTag() {
    if (!this.selectedTag.name) {
      this.toastr.warning('Tag Name is required');
      return;
    }

    const isNew = this.selectedTag.id === 0;
    
    if (isNew) {
      this.tagDefinitionService.createTag(this.selectedTag).subscribe(result => {
        this.toastr.success('Tag created successfully');
        // Reload the selected tag definition to refresh tags list
        if (this.selectedTagDefinition && this.selectedTagDefinition.id > 0) {
          this.tagDefinitionService.getById(this.selectedTagDefinition.id).subscribe(fullData => {
            this.selectedTagDefinition = fullData;
          });
        }
        this.isEditingTag = false;
        this.selectedTag = null;
      }, error => {
        this.toastr.error('Failed to create tag');
      });
    } else {
      this.tagDefinitionService.updateTag(this.selectedTag).subscribe(result => {
        this.toastr.success('Tag updated successfully');
        // Reload the selected tag definition to refresh tags list
        if (this.selectedTagDefinition && this.selectedTagDefinition.id > 0) {
          this.tagDefinitionService.getById(this.selectedTagDefinition.id).subscribe(fullData => {
            this.selectedTagDefinition = fullData;
          });
        }
        this.isEditingTag = false;
        this.selectedTag = null;
      }, error => {
        this.toastr.error('Failed to update tag');
      });
    }
  }

  cancelEditTag() {
    this.isEditingTag = false;
    this.selectedTag = null;
  }

  deleteTag(tag: Tag) {
    swal({
      title: 'Are you sure?',
      text: `Do you want to delete "${tag.name}"?`,
      icon: 'warning',
      buttons: {
        cancel: {
          text: 'No',
          visible: true,
          closeModal: true
        },
        confirm: {
          text: 'Yes',
          value: true,
          visible: true,
          className: 'bg-danger',
          closeModal: true
        }
      }
    }).then((isConfirm) => {
      if (isConfirm) {
        this.tagDefinitionService.deleteTag(tag.id).subscribe(() => {
          this.toastr.success('Tag deleted successfully');
          // Reload the selected tag definition to refresh tags list
          if (this.selectedTagDefinition && this.selectedTagDefinition.id > 0) {
            this.tagDefinitionService.getById(this.selectedTagDefinition.id).subscribe(fullData => {
              this.selectedTagDefinition = fullData;
            });
          }
          if (this.selectedTag?.id === tag.id) {
            this.selectedTag = null;
          }
        }, () => {
          this.toastr.error('Failed to delete tag');
        });
      }
    });
  }
}
